import { useRef, useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  TrendingUp,
  Clock,
  HandCoins,
  Target,
  Mail,
  UserSearch,
  MessageSquareHeart,
  Briefcase,
} from 'lucide-react';

interface Department {
  name: string;
  role: string;
  icon: React.ReactNode;
  hoursSaved: number;
  annualValue: [number, number];
  roi: number;
  description: string;
  tasks: string[];
  color: string;
  bgColor: string;
}

const departments: Department[] = [
  {
    name: 'Fundraising',
    role: 'AI Fundraiser',
    icon: <HandCoins className="w-6 h-6" />,
    hoursSaved: 35,
    annualValue: [45500, 182000],
    roi: 2257,
    description:
      'Replaces expensive fundraising retainers + success fees. Builds investor lists, drafts outreach, tracks pipeline.',
    tasks: ['Investor list building', 'Outreach sequencing', 'Pitch deck drafts', 'Pipeline tracking'],
    color: 'text-amber-400',
    bgColor: 'from-amber-500/10 to-orange-500/10 border-amber-500/20',
  },
  {
    name: 'Sales',
    role: 'AI Sales Leader',
    icon: <Target className="w-6 h-6" />,
    hoursSaved: 45.5,
    annualValue: [59150, 236600],
    roi: 2935,
    description:
      'Prospects, scores leads, drafts personalized emails, books meetings, updates CRM. Never misses a follow-up.',
    tasks: ['Lead scoring', 'Email personalization', 'Meeting booking', 'CRM updates'],
    color: 'text-teal-400',
    bgColor: 'from-teal-500/10 to-cyan-500/10 border-teal-500/20',
  },
  {
    name: 'Recruiting',
    role: 'AI Recruiter',
    icon: <UserSearch className="w-6 h-6" />,
    hoursSaved: 30,
    annualValue: [39000, 156000],
    roi: 1933,
    description:
      'Sources candidates, screens resumes, schedules interviews, sends rejections. Full pipeline automation.',
    tasks: ['Candidate sourcing', 'Resume screening', 'Interview scheduling', 'Offer letters'],
    color: 'text-emerald-400',
    bgColor: 'from-emerald-500/10 to-green-500/10 border-emerald-500/20',
  },
  {
    name: 'Investor Relations',
    role: 'AI IR Manager',
    icon: <MessageSquareHeart className="w-6 h-6" />,
    hoursSaved: 20,
    annualValue: [26000, 104000],
    roi: 1287,
    description:
      'Updates investors, prepares board decks, tracks KPIs, manages cap table communications.',
    tasks: ['Investor updates', 'Board decks', 'KPI tracking', 'Cap table comms'],
    color: 'text-indigo-400',
    bgColor: 'from-indigo-500/10 to-violet-500/10 border-indigo-500/20',
  },
  {
    name: 'CEO Assistant',
    role: 'AI Chief of Staff',
    icon: <Briefcase className="w-6 h-6" />,
    hoursSaved: 19,
    annualValue: [24700, 98800],
    roi: 1223,
    description:
      'Calendar management, email triage, meeting prep, travel booking, expense reports. Your always-on executive assistant.',
    tasks: ['Calendar mgmt', 'Email triage', 'Meeting prep', 'Expense reports'],
    color: 'text-rose-400',
    bgColor: 'from-rose-500/10 to-pink-500/10 border-rose-500/20',
  },
  {
    name: 'Email Marketing',
    role: 'AI Email Marketer',
    icon: <Mail className="w-6 h-6" />,
    hoursSaved: 15,
    annualValue: [19500, 78000],
    roi: 967,
    description:
      'Segments lists, writes sequences, A/B tests subject lines, tracks open rates. Full lifecycle marketing.',
    tasks: ['List segmentation', 'Sequence writing', 'A/B testing', 'Analytics'],
    color: 'text-sky-400',
    bgColor: 'from-sky-500/10 to-blue-500/10 border-sky-500/20',
  },
];

const toK = (annualValue: number) => Math.round(annualValue / 100) / 10;

function AnimatedCounter({
  target,
  suffix = '',
  prefix = '',
  duration = 2000,
  decimals = 0,
  start,
}: {
  target: number;
  suffix?: string;
  prefix?: string;
  duration?: number;
  decimals?: number;
  start: boolean;
}) {
  const [value, setValue] = useState(target);

  useEffect(() => {
    if (start || (window as { __PRERENDER__?: boolean }).__PRERENDER__) return;
    setValue(0);
  }, []);

  useEffect(() => {
    if (!start) return;
    let raf: number;
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(eased * target);
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);

  return (
    <span>
      {prefix}
      {decimals > 0 ? value.toFixed(decimals) : Math.round(value).toLocaleString()}
      {suffix}
    </span>
  );
}

export const DepartmentCards = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const totalHoursSaved = departments.reduce((sum, dept) => sum + dept.hoursSaved, 0);
  const totalAnnualValue = departments.reduce((sum, dept) => sum + dept.annualValue[0], 0);

  return (
    <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="text-center mb-16"
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-500/10 dark:text-teal-400 rounded-full text-sm font-medium border dark:border-teal-500/20 mb-6">
          <Clock className="w-4 h-4" />
          Time Savings by Department
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
          Hire Specialists, Not Generalists
        </h2>
        <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
          Each bot is trained for a specific role. They work 24/7, never take vacation, and get better every week.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {departments.map((dept, idx) => (
          <motion.div
            key={dept.name}
            className={`group relative rounded-xl border bg-gradient-to-br ${dept.bgColor} backdrop-blur-sm overflow-hidden hover:shadow-lg transition-all duration-300`}
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: idx * 0.1 }}
            whileHover={{ y: -4 }}
          >
            {/* Glow effect on hover */}
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background: `radial-gradient(circle at 50% 0%, ${dept.color.replace('text-', '').replace('-400', '')}20, transparent 70%)`,
              }}
            />

            <div className="relative p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className={`w-11 h-11 rounded-lg bg-slate-100 dark:bg-slate-800/60 flex items-center justify-center ${dept.color}`}>
                  {dept.icon}
                </div>
                <span className="text-xs font-medium text-slate-500 bg-slate-100 dark:bg-slate-800/60 px-2 py-1 rounded-full">
                  {dept.role}
                </span>
              </div>

              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">{dept.name}</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-5 leading-relaxed">{dept.description}</p>

              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="text-center p-2 rounded-lg bg-slate-50 dark:bg-slate-800/40">
                  <div className={`text-xl font-bold ${dept.color}`}>
                    <AnimatedCounter
                      target={dept.hoursSaved}
                      suffix="h"
                      decimals={dept.hoursSaved % 1 !== 0 ? 1 : 0}
                      start={isInView}
                    />
                  </div>
                  <div className="text-label-2xs text-slate-500 mt-0.5">saved/wk</div>
                </div>
                <div className="text-center p-2 rounded-lg bg-slate-50 dark:bg-slate-800/40">
                  <div className={`text-xl font-bold ${dept.color}`}>
                    <AnimatedCounter
                      target={toK(dept.annualValue[0])}
                      prefix="$"
                      suffix="K"
                      decimals={toK(dept.annualValue[0]) % 1 !== 0 ? 1 : 0}
                      start={isInView}
                    />
                  </div>
                  <div className="text-label-2xs text-slate-500 mt-0.5">value/yr</div>
                </div>
                <div className="text-center p-2 rounded-lg bg-slate-50 dark:bg-slate-800/40">
                  <div className={`text-xl font-bold ${dept.color}`}>
                    <AnimatedCounter target={dept.roi} suffix="%" start={isInView} />
                  </div>
                  <div className="text-label-2xs text-slate-500 mt-0.5">ROI</div>
                </div>
              </div>

              {/* Tasks */}
              <div className="space-y-1.5">
                {dept.tasks.map((task) => (
                  <div key={task} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                    <div className="w-1 h-1 rounded-full bg-teal-500/60 dark:bg-teal-400/60" />
                    {task}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Total Summary Bar */}
      <motion.div
        className="mt-10 p-6 rounded-xl border border-teal-500/20 bg-gradient-to-r from-teal-500/5 to-cyan-500/5 backdrop-blur-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.8 }}
      >
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-teal-400" />
            </div>
            <div>
              <div className="text-sm text-slate-600 dark:text-slate-400">Combined across all departments</div>
              <div className="text-lg font-bold text-slate-900 dark:text-white">
                {totalHoursSaved % 1 !== 0 ? totalHoursSaved.toFixed(1) : totalHoursSaved} hours saved per week
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-teal-400">
              <AnimatedCounter target={totalAnnualValue} prefix="$" start={isInView} duration={2500} />
            </div>
            <div className="text-sm text-slate-500">minimum annual value created</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
